import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError, api, apiBlob, mensagemErro } from '../api';
import { Carregando, Erro, Icon, Progresso, Toast, useToast } from '../components/ui';
import { formatarWhatsapp } from '../format';
import { copiarImagem, paraPng, podeCopiarImagem } from '../imagem';
import { abrirNoApp } from '../whatsapp';

interface ClienteResumo { id: string; nome: string; empresa: string | null; cidade: string | null; whatsapp: string }
interface Aberto { id: string; abertoEm: string | null; cliente: ClienteResumo }
interface Proximo extends Aberto { mensagem: string; faltando: string[]; url: string | null; erro: string | null }
interface Lote {
  campanha: { id: string; nome: string; status: string; temImagem: boolean };
  progresso: { total: number; enviados: number; pendentes: number };
  abertos: Aberto[];
  proximos: Proximo[];
  bloqueados: number;
}
interface ResultadoConfirmar {
  confirmados: number; repetidos: number; devolvidos: number;
  falhas: Array<{ id: string; code: string; detail: string }>;
}

type EstadoItem = 'fila' | 'abrindo' | 'aberto' | 'bloqueado' | 'erro';

const TAMANHOS = [10, 20, 30, 50];
const lerTamanho = () => {
  try {
    const n = Number(localStorage.getItem('crm.lote.tamanho'));
    return TAMANHOS.includes(n) ? n : 20;
  } catch {
    return 20;
  }
};

/**
 * Modo lote — app WhatsApp Desktop.
 * 1) Abrir próximo (Enter): o app pula para a conversa com o texto pronto (fica como rascunho).
 * 2) Vendedor envia no app (na hora ou todos no fim).
 * 3) Confirma aqui quem recebeu; desmarcados voltam para a fila.
 */
export function LotePage() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const [tamanho, setTamanho] = useState(lerTamanho);
  const [lote, setLote] = useState<Lote | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [estados, setEstados] = useState<Record<string, EstadoItem>>({});
  const [abertos, setAbertos] = useState<Aberto[]>([]);
  const [marcados, setMarcados] = useState<Record<string, boolean>>({});
  const [followUp, setFollowUp] = useState(true);
  const [confirmando, setConfirmando] = useState(false);
  const [imagem, setImagem] = useState<Blob | null>(null);
  const [semApp, setSemApp] = useState(0); // aberturas seguidas sem o app tomar o foco

  const carregar = useCallback(async (n = tamanho) => {
    setCarregando(true);
    setErro(null);
    try {
      const r = await api<Lote>(`/campanhas/${id}/lote`, { body: { tamanho: n } });
      setLote(r);
      setAbertos(r.abertos);
      setMarcados(Object.fromEntries(r.abertos.map((a) => [a.id, true])));
      setEstados(Object.fromEntries(r.proximos.map((p) => [p.id, p.erro ? 'erro' : 'fila'] as const)));
    } catch (e) {
      setErro(mensagemErro(e));
    } finally {
      setCarregando(false);
    }
  }, [id, tamanho]);

  useEffect(() => {
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Imagem da campanha: PNG pronto antes do clique (a cópia precisa acontecer com a aba em foco).
  // É a mesma para todos, então colar no fim (rascunhos) também funciona.
  const temImagem = !!lote?.campanha.temImagem;
  useEffect(() => {
    if (!temImagem || imagem) return;
    apiBlob(`/campanhas/${id}/imagem`).then(paraPng).then(setImagem).catch(() => setErro('Não foi possível carregar a imagem da campanha.'));
  }, [temImagem, imagem, id]);

  const proximos = lote?.proximos ?? [];
  const atual = useMemo(() => proximos.find((p) => estados[p.id] === 'fila' && p.url), [proximos, estados]);
  const feitos = proximos.filter((p) => estados[p.id] === 'aberto').length;
  const emExecucao = lote?.campanha.status === 'EM_EXECUCAO';

  const abrirProximo = useCallback(async () => {
    if (!atual?.url) return;
    const item = atual;
    setEstados((s) => ({ ...s, [item.id]: 'abrindo' }));
    if (imagem && !(await copiarImagem(imagem))) toast.show('Não consegui copiar a imagem. Anexe pelo clipe no WhatsApp.');
    const abriuPromise = abrirNoApp(item.url!); // dentro do gesto do usuário
    try {
      await api(`/campanha-itens/${item.id}/abrir`, { body: { destino: 'APP' } });
      setEstados((s) => ({ ...s, [item.id]: 'aberto' }));
      setAbertos((a) => (a.some((x) => x.id === item.id) ? a : [...a, { id: item.id, abertoEm: new Date().toISOString(), cliente: item.cliente }]));
      setMarcados((m) => ({ ...m, [item.id]: true }));
    } catch (e) {
      if (e instanceof ApiError && e.code === 'CLIENTE_NAO_CONTACTAR') {
        setEstados((s) => ({ ...s, [item.id]: 'bloqueado' }));
        toast.show(`${item.cliente.nome} pediu para não ser contatado. NÃO envie o rascunho dele no WhatsApp.`);
      } else {
        setEstados((s) => ({ ...s, [item.id]: 'erro' }));
        setErro(mensagemErro(e));
      }
    }
    // a 1ª vez o Chrome mostra "Abrir WhatsApp?" e a janela não perde o foco: só avisa após 2 falhas seguidas
    const abriu = await abriuPromise;
    setSemApp((n) => (abriu ? 0 : n + 1));
  }, [atual, toast, imagem]);

  // Enter = abrir próximo (fora de campos de texto)
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      const alvo = e.target as HTMLElement;
      if (e.key !== 'Enter' || confirmando || alvo.closest('input, textarea, select, button')) return;
      e.preventDefault();
      if (emExecucao) void abrirProximo();
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [abrirProximo, confirmando, emExecucao]);

  async function confirmar() {
    const enviados = abertos.filter((a) => marcados[a.id]).map((a) => a.id);
    const devolver = abertos.filter((a) => !marcados[a.id]).map((a) => a.id);
    setConfirmando(true);
    setErro(null);
    const total: ResultadoConfirmar = { confirmados: 0, repetidos: 0, devolvidos: 0, falhas: [] };
    try {
      // a API aceita até 100 de cada por chamada
      for (let i = 0; i < Math.max(enviados.length, devolver.length); i += 100) {
        const r = await api<ResultadoConfirmar>(`/campanhas/${id}/lote/confirmar`, {
          body: { enviados: enviados.slice(i, i + 100), devolver: devolver.slice(i, i + 100), followUpDias: followUp ? 2 : 0 },
        });
        total.confirmados += r.confirmados;
        total.repetidos += r.repetidos;
        total.devolvidos += r.devolvidos;
        total.falhas.push(...r.falhas);
      }
      const partes = [`${total.confirmados + total.repetidos} confirmados`];
      if (total.devolvidos) partes.push(`${total.devolvidos} voltaram para a fila`);
      toast.show(partes.join(' · ') + '.');
      if (total.falhas.length) setErro(`Não registrados (${total.falhas.length}): ${[...new Set(total.falhas.map((f) => f.detail))].join(' ')}`);
      await carregar();
    } catch (e) {
      setErro(mensagemErro(e));
    } finally {
      setConfirmando(false);
    }
  }

  if (carregando && !lote) return <Carregando />;
  if (!lote) return <div className="col"><Erro erro={erro} /><Link to={`/campanhas/${id}`}>Voltar para a campanha</Link></div>;

  const pg = lote.progresso;
  const marcadosN = abertos.filter((a) => marcados[a.id]).length;

  return (
    <div className="col" style={{ gap: 18, maxWidth: 980 }}>
      <div>
        <Link to={`/campanhas/${lote.campanha.id}`} className="small">← {lote.campanha.nome}</Link>
        <h1 style={{ margin: '6px 0 2px' }}>Modo lote · WhatsApp Desktop</h1>
        <span className="small muted num">{pg.enviados} de {pg.total} enviados · faltam {pg.pendentes}</span>
      </div>
      <Erro erro={erro} />

      <div className="alert info" style={{ flexDirection: 'column', gap: 6 }}>
        <b>Como funciona</b>
        <span>1. Aperte <b>Enter</b> (ou “Abrir próximo”): o app do WhatsApp abre a conversa com a mensagem pronta. Na primeira vez o Chrome pergunta “Abrir WhatsApp?” — marque <b>Sempre permitir</b>.</span>
        <span>2. Envie no app na hora, ou abra todos e envie no fim — os não enviados ficam como <b>rascunho</b> em cada conversa.</span>
        <span>3. Volte aqui (Alt+Tab) e aperte Enter para o próximo. No fim, <b>confirme quem recebeu</b>.</span>
      </div>
      {temImagem && (
        <div className="alert" style={{ gap: 12, alignItems: 'center' }}>
          <span style={{ flex: 1 }}>
            Esta campanha tem imagem. Cada vez que abrir um cliente ela vai copiada: na conversa, aperte <b>Ctrl+V</b> antes de enviar.
            {!podeCopiarImagem() && ' (Este navegador não copia imagens — anexe pelo clipe.)'}
          </span>
          {imagem && podeCopiarImagem() && (
            <button type="button" className="btn sm" onClick={async () => toast.show((await copiarImagem(imagem)) ? 'Imagem copiada.' : 'Não foi possível copiar.')}>Copiar imagem</button>
          )}
        </div>
      )}
      {semApp >= 2 && (
        <div className="alert warn">
          O WhatsApp não parece ter aberto. Confira se o <b>WhatsApp Desktop</b> está instalado e se o Chrome não bloqueou o aviso “Abrir WhatsApp?”.
        </div>
      )}

      <div className="row" style={{ gap: 18 }}>
        {/* ---------------------------------------------------------- preparar */}
        <section className="card" style={{ flex: '1 1 420px' }}>
          <div className="card-head">
            <h2 style={{ margin: 0 }}>Abrir no app</h2>
            <label className="small muted" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              Lote de
              <select className="select" style={{ height: 30, width: 70 }} value={tamanho}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  setTamanho(n);
                  try { localStorage.setItem('crm.lote.tamanho', String(n)); } catch { /* sem storage */ }
                  void carregar(n);
                }}>
                {TAMANHOS.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
          </div>

          {!emExecucao ? (
            <div className="alert warn">A campanha não está em execução. Você ainda pode confirmar os já abertos.</div>
          ) : proximos.length === 0 ? (
            <div className="empty" style={{ padding: 20 }}>
              <Icon name="check" size={26} color="var(--green-text)" />
              <span>Nenhum cliente pendente na sua fila.</span>
            </div>
          ) : (
            <>
              <Progresso valor={feitos} total={proximos.length} />
              <button className="btn primary xl" onClick={() => void abrirProximo()} disabled={!atual || Object.values(estados).includes('abrindo')}>
                <Icon name="whatsapp" size={20} />
                {atual ? `Abrir próximo: ${atual.cliente.nome} (Enter)` : 'Lote aberto — confirme ao lado'}
              </button>
              <ol style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 420, overflow: 'auto' }}>
                {proximos.map((p) => {
                  const st = estados[p.id];
                  return (
                    <li key={p.id} style={{
                      display: 'flex', gap: 10, alignItems: 'center', padding: '7px 10px', borderRadius: 8,
                      background: p.id === atual?.id ? 'var(--blue-bg)' : 'transparent',
                    }}>
                      <span style={{ width: 18, display: 'inline-flex' }}>
                        {st === 'aberto' ? <Icon name="check" size={15} color="var(--green-text)" /> : st === 'bloqueado' || st === 'erro' ? <Icon name="alerta" size={15} color="var(--red-text)" /> : null}
                      </span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.cliente.nome}</span>
                        <span className="small muted">{[p.cliente.empresa, formatarWhatsapp(p.cliente.whatsapp)].filter(Boolean).join(' · ')}</span>
                      </span>
                      <span className="small" style={{ color: st === 'bloqueado' || st === 'erro' ? 'var(--red-text)' : 'var(--faint)' }}>
                        {st === 'aberto' ? 'aberto' : st === 'abrindo' ? 'abrindo…' : st === 'bloqueado' ? 'não contactar — não envie' : p.erro === 'MENSAGEM_LONGA' ? 'mensagem longa demais' : st === 'erro' ? 'erro' : ''}
                      </span>
                    </li>
                  );
                })}
              </ol>
            </>
          )}
        </section>

        {/* ---------------------------------------------------------- confirmar */}
        <section className="card" style={{ flex: '1 1 380px' }}>
          <h2 style={{ margin: 0 }}>Confirmar envios</h2>
          {abertos.length === 0 ? (
            <span className="muted small">Os clientes abertos no app aparecem aqui para você confirmar quem recebeu.</span>
          ) : (
            <>
              <span className="small muted">Desmarque quem você <b>não</b> enviou — volta para a fila.</span>
              <label className="check small" style={{ gap: 8 }}>
                <input type="checkbox" checked={marcadosN === abertos.length}
                  onChange={(e) => setMarcados(Object.fromEntries(abertos.map((a) => [a.id, e.target.checked])))} />
                Marcar todos ({abertos.length})
              </label>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 360, overflow: 'auto' }}>
                {abertos.map((a) => (
                  <li key={a.id}>
                    <label className="check" style={{ padding: '6px 4px', gap: 10 }}>
                      <input type="checkbox" checked={!!marcados[a.id]} onChange={(e) => setMarcados((m) => ({ ...m, [a.id]: e.target.checked }))} />
                      <span style={{ minWidth: 0 }}>
                        {a.cliente.nome}
                        <span className="small muted"> · {a.cliente.empresa ?? formatarWhatsapp(a.cliente.whatsapp)}</span>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
              <label className="check small" style={{ gap: 8 }}>
                <input type="checkbox" checked={followUp} onChange={(e) => setFollowUp(e.target.checked)} />
                Agendar retorno em 2 dias para os confirmados
              </label>
              <button className="btn primary lg" onClick={() => void confirmar()} disabled={confirmando}>
                {confirmando ? 'Registrando…' : `Confirmar ${marcadosN} enviado${marcadosN === 1 ? '' : 's'}`
                  + (abertos.length - marcadosN ? ` · devolver ${abertos.length - marcadosN}` : '')}
              </button>
              <span className="small muted">Interessado/oportunidade você marca depois na ficha ou na fila, como hoje.</span>
            </>
          )}
        </section>
      </div>
      <Toast msg={toast.msg} onDone={toast.clear} />
    </div>
  );
}
